import { FileText } from "@phosphor-icons/react";

function Table({ head, rows, testid, statusCol }) {
  return (
    <div className="bg-[var(--c-bg)] border border-[var(--c-border)] rounded-sm overflow-x-auto" data-testid={testid}>
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-[var(--c-border)]">
            {head.map((h) => <th key={h} className="text-left px-3 py-2 text-[9px] font-mono tracking-widest uppercase text-[var(--c-muted)] whitespace-nowrap">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-[var(--c-border)]/50 last:border-0">
              {r.map((c, j) => {
                const isStatus = statusCol && j === r.length - 1;
                const color = !isStatus ? "" : c === "pass" || c === "PASS" ? "text-[var(--c-green)]" : c === "fail" || c === "FAIL" ? "text-[var(--c-red)]" : "text-[var(--c-muted)]";
                return <td key={j} className={`px-3 py-1.5 whitespace-nowrap ${j === 0 ? "text-[var(--c-text)]" : `font-mono ${color || "text-[var(--c-muted)]"}`}`}>{c ?? "—"}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function PreviewPane({ selected, data }) {
  if (!selected) {
    return (
      <div className="bg-[var(--c-surface)] border border-dashed border-[var(--c-border)] rounded-sm p-14 text-center" data-testid="preview-empty">
        <FileText size={28} className="mx-auto text-[var(--c-muted)]" />
        <div className="text-sm text-[var(--c-text)] mt-3">Select a document from the tree</div>
        <div className="text-xs text-[var(--c-muted)] mt-1">Generate stage outputs from the status panel on the right.</div>
      </div>
    );
  }

  if (selected.key === "design-sheet" && data.design_sheet) {
    return (
      <div className="space-y-4" data-testid="preview-design-sheet">
        {data.design_sheet.sections.map((s) => (
          <div key={s.id}>
            <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-accent)] mb-1.5">{s.title}</div>
            <Table testid={`sheet-section-${s.id}`} statusCol
              head={["Parameter", "Unit", "Guaranteed", "Designed", "Margin %", "Status"]}
              rows={s.rows.map((r) => [r.parameter, r.unit, r.guaranteed, r.designed, r.margin_pct ?? "—", r.status])} />
          </div>
        ))}
      </div>
    );
  }

  if (selected.key === "design-output-notes" && data.design_output_notes) {
    return (
      <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-6" data-testid="preview-notes">
        <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)] mb-3">
          Design Output Notes · {String(data.design_output_notes.generated_at).slice(0, 16).replace("T", " ")} · {data.design_output_notes.model}
        </div>
        <div className="text-xs text-[var(--c-text)] leading-relaxed whitespace-pre-wrap">
          {data.design_output_notes.text.replace(/^#{1,3} /gm, "").replace(/\*\*/g, "")}
        </div>
      </div>
    );
  }

  if (selected.pack) {
    const p = selected.pack;
    return (
      <div className="space-y-4" data-testid="preview-pack">
        <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)]">
          To: {p.sent_to.map((t) => t.name).join(", ")} · sent {String(p.sent_at).slice(0, 16).replace("T", " ")}
        </div>
        {p.sections.map((s) => (
          <div key={s.key}>
            <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-accent)] mb-1.5">{s.title}</div>
            <Table testid={`pack-section-${s.key}`} head={["Item", "Value"]} rows={s.rows} />
          </div>
        ))}
        {p.constraint_analysis && (
          <div className="bg-[var(--c-accent)]/10 border border-[var(--c-accent)]/40 rounded-sm p-4" data-testid="pack-constraint-analysis">
            <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-accent)] mb-2">AI Copilot · constraint impact analysis</div>
            <div className="text-xs text-[var(--c-text)] whitespace-pre-wrap">{p.constraint_analysis.analysis}</div>
            {(p.constraint_analysis.suggestions || []).map((s, i) => (
              <div key={i} className="mt-2 text-xs text-[var(--c-muted)] border border-[var(--c-border)] rounded-sm px-3 py-2">
                <span className="text-[var(--c-text)]">{s.label}</span>
                {s.inputs_patch && <span className="font-mono ml-2">{JSON.stringify(s.inputs_patch)}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (selected.note) {
    const n = selected.note;
    return (
      <div className="space-y-4" data-testid="preview-note">
        <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)]">
          {n.discipline} · sent {String(n.sent_at).slice(0, 16).replace("T", " ")} to {n.sent_to.map((t) => t.name).join(", ")}
        </div>
        <Table testid="note-rows" head={["Item", "Value"]} rows={n.rows} />
        <Table testid="note-reads" head={["User", "Read at"]}
          rows={n.sent_to.map((t) => {
            const r = n.reads.find((x) => x.user_id === t.user_id);
            return [t.name, r ? String(r.at).slice(0, 16).replace("T", " ") : "Unread"];
          })} />
      </div>
    );
  }

  if (selected.doc) {
    const d = selected.doc;
    if (d.type === "ga") {
      return (
        <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-3" data-testid="preview-ga"
          dangerouslySetInnerHTML={{ __html: d.svg }} />
      );
    }
    return (
      <div data-testid={`preview-doc-${d.id}`}>
        <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)] mb-2">{d.title} · Rev {data.approval_package.rev}</div>
        <Table testid={`doc-table-${d.id}`} head={d.head} rows={d.rows} statusCol={d.id === "compliance"} />
      </div>
    );
  }

  return (
    <div className="bg-[var(--c-surface)] border border-dashed border-[var(--c-border)] rounded-sm p-14 text-center" data-testid="preview-not-generated">
      <FileText size={28} className="mx-auto text-[var(--c-muted)]" />
      <div className="text-sm text-[var(--c-text)] mt-3">Not generated yet</div>
      <div className="text-xs text-[var(--c-muted)] mt-1">Use the action panel to generate this document.</div>
    </div>
  );
}
